import type { Dictionary } from '@/core/providers/dictionary-provider';

import type { LocationDetailDict } from './location-detail-types';
import type { OverviewHealthLabels } from './location-detail-overview-tab';
import type { LocationHealthLabels } from './location-health-indicators';
import type { LocationReviewInsightsLabels } from './location-review-insights';

export function buildOverviewHealthLabels(
  d: LocationDetailDict | undefined,
): OverviewHealthLabels {
  return {
    title: d?.overview?.healthTitle ?? 'Location health',
    googleLabel: d?.overview?.googleLabel ?? 'Reviews',
    linksLabel: d?.overview?.linksLabel ?? 'Links',
    productsLabel: d?.overview?.productsLabel ?? 'Products',
    linksOk: d?.overview?.linksOk ?? 'Set',
    linksEmpty: d?.overview?.linksEmpty ?? 'None',
    productsCount: d?.overview?.productsCount ?? '{count} linked',
    productsNone: d?.overview?.productsNone ?? 'None linked',
    googleLinked: d?.overview?.googleOk ?? 'Synced',
    googleListing:
      d?.overview?.googleListing ??
      d?.integrations?.googlePlaces?.listingLabel ??
      'Listing only',
    googleNeedsAction:
      d?.overview?.googleNeedsAction ??
      d?.integrations?.googlePlaces?.needsActionLabel ??
      'Needs action',
    googleOptOut: d?.overview?.googleOptOut ?? 'Not used',
    googleSyncFailed: d?.overview?.googleSyncFailed ?? 'Sync failed',
    ctaLinkGoogle: d?.overview?.ctaLinkGoogle ?? 'Link review listing',
    ctaFixSync: d?.overview?.ctaFixSync ?? 'Check sync',
    ctaAddLinks: d?.overview?.ctaAddLinks ?? 'Add links',
    ctaViewProducts: d?.overview?.ctaViewProducts ?? 'View products',
    ctaEditInfo: d?.overview?.ctaEditInfo ?? 'Edit information',
    allClear: d?.overview?.allClear ?? 'Everything looks healthy.',
  };
}

export function buildListHealthLabels(
  overview: OverviewHealthLabels,
): LocationHealthLabels {
  return {
    googleLinked: overview.googleLinked,
    googleListing: overview.googleListing,
    googleNeedsAction: overview.googleNeedsAction,
    googleOptOut: overview.googleOptOut,
    googleSyncFailed: overview.googleSyncFailed,
    linksOk: overview.linksOk,
    linksEmpty: overview.linksEmpty,
    googleChannel: overview.googleLabel,
    linksChannel: overview.linksLabel,
    ariaGroup: overview.title,
  };
}

export function buildReviewInsightsLabels(
  d: LocationDetailDict | undefined,
  dict: Dictionary,
): LocationReviewInsightsLabels {
  const dashSummary = dict.reputation?.dashboard?.summary;
  return {
    title:
      d?.overview?.reviewInsightsTitle ?? d?.overview?.googleLabel ?? 'Reviews',
    summary:
      d?.overview?.reviewInsightsSummary ??
      (
        dict as {
          locations?: { cardReviewSummary?: string };
        }
      ).locations?.cardReviewSummary ??
      '★ {rating} · {count} reviews',
    summaryAria:
      d?.overview?.reviewInsightsSummaryAria ??
      (
        dict as {
          locations?: { cardReviewSummaryAria?: string };
        }
      ).locations?.cardReviewSummaryAria ??
      '{rating} average from {count} reviews',
    reviewsCount: d?.overview?.reviewInsightsCount ?? '{count} reviews',
    emptySynced:
      d?.overview?.reviewInsightsEmptySynced ??
      'Sync is connected — no reviews yet.',
    listingOnly:
      d?.overview?.reviewInsightsListingOnly ??
      'Listing linked. Connect review sync to see rating and volume here.',
    noSummary:
      d?.overview?.reviewInsightsNoSummary ??
      'No AI summary yet. Open reputation for the full picture.',
    openReputation: d?.overview?.reviewInsightsOpen ?? 'Open reputation',
    ctaConnectSync:
      d?.overview?.reviewInsightsConnectSync ?? 'Connect review sync',
    aiSummary: {
      title:
        dashSummary?.title ??
        d?.overview?.reviewInsightsAiTitle ??
        'AI summary',
      loading: dashSummary?.loading ?? 'Analyzing your reviews…',
      unavailable:
        dashSummary?.unavailable ?? 'AI summary is not available right now.',
      nextSteps: dashSummary?.nextSteps ?? 'Suggested next steps',
      openReviews: dashSummary?.openReviews ?? 'View all reviews',
      updatedAgo: dashSummary?.updatedAgo ?? 'Updated {time}',
    },
  };
}
